import { CaretRight } from '@/core/ui/icons';
import {
  Badge,
  Button,
  cn,
  EmptyState,
  ErrorState,
  FilterMenu,
  Page,
  PageHeader,
  Skeleton,
  type FilterOption,
} from '@/core/ui';
import { PAGE_SIZE, useVisits } from '../application/queries';
import {
  availabilityText,
  formatDate,
  formatVisitWhen,
  modalityLabel,
  placeText,
  STATUS_LABELS,
  STATUS_TONES,
  STATUSES,
  VisitStatus,
  type Visit,
  type VisitStatusCode,
} from '../domain/visit';
import { VisitDetail } from './VisitDetail';

export interface VisitsPageFilters {
  status?: VisitStatusCode;
  /** Página, começando em 1. */
  page: number;
  /** O pedido aberto no painel. */
  selected?: string;
}

interface Props {
  filters: VisitsPageFilters;
  onChange: (next: VisitsPageFilters) => void;
}

/**
 * Pedidos de visita do portal, do mais novo para o mais antigo. Clicar abre
 * o painel com o contato (WhatsApp), quando a pessoa pode e as ações do time.
 * Filtros e o pedido aberto vão na URL — dá para mandar o link a um colega.
 */
export function VisitsPage({ filters, onChange }: Props) {
  const { status, page, selected } = filters;
  const visits = useVisits({ status, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
  const counts = visits.data?.counts ?? {};
  const all = Object.values(counts).reduce((a, b) => a + b, 0);

  const options: FilterOption<VisitStatusCode | 'todos'>[] = [
    { value: 'todos', label: 'Todos', count: visits.data ? all : undefined },
    ...STATUSES.map((s) => ({ value: s.value, label: s.label, count: counts[s.value] })),
  ];

  const total = visits.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const items = visits.data?.items ?? [];

  return (
    <Page>
      <PageHeader
        title="Visitas"
        description="Pedidos de visita feitos pelos profissionais no portal. Chame no WhatsApp, agende com um analista e registre como foi."
      />

      <div className={cn('grid gap-6', selected && 'lg:grid-cols-[minmax(0,1fr)_minmax(0,27rem)]')}>
        <div className={cn(selected && 'hidden lg:block')}>
          <FilterMenu
            label="Status"
            className="mb-4"
            value={status ?? 'todos'}
            options={options}
            onChange={(v) => onChange({ status: v === 'todos' ? undefined : v, page: 1, selected })}
          />

          {visits.isPending ? (
            <Skeleton className="h-72 rounded-2xl" />
          ) : visits.isError ? (
            <ErrorState error={visits.error} onRetry={() => void visits.refetch()} />
          ) : items.length === 0 ? (
            <EmptyState
              title={status ? 'Nenhum pedido com este status' : 'Nenhum pedido de visita ainda'}
              description={
                status ? undefined : 'Quando um profissional pedir uma visita no portal, ela aparece aqui.'
              }
            />
          ) : (
            <>
              <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">
                {items.map((v) => (
                  <Row
                    key={v.id}
                    visit={v}
                    active={v.id === selected}
                    onOpen={() => onChange({ ...filters, selected: v.id })}
                  />
                ))}
              </ul>
              {pages > 1 && (
                <nav aria-label="Páginas" className="mt-4 flex items-center justify-between gap-3 text-sm">
                  <span className="text-fg-subtle">
                    {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => onChange({ ...filters, page: page - 1 })}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page >= pages}
                      onClick={() => onChange({ ...filters, page: page + 1 })}
                    >
                      Próxima
                    </Button>
                  </div>
                </nav>
              )}
            </>
          )}
        </div>

        {selected && (
          <aside aria-label="Pedido selecionado" className="lg:sticky lg:top-6 lg:self-start">
            <VisitDetail visitId={selected} onBack={() => onChange({ ...filters, selected: undefined })} />
          </aside>
        )}
      </div>
    </Page>
  );
}

/** Uma linha por pedido: quem, de onde, quando pode — e, se agendada, quando é. */
function Row({ visit: v, active, onOpen }: { visit: Visit; active: boolean; onOpen: () => void }) {
  const scheduled = v.status === VisitStatus.SCHEDULED && v.scheduled_for;
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        aria-current={active || undefined}
        className={cn(
          'group flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-surface-2',
          active && 'bg-surface-2',
        )}
      >
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate text-sm font-medium text-fg">
              {v.requester_name || v.requester_email}
            </span>
            <span className="text-xs text-fg-subtle">· {placeText(v)}</span>
          </p>
          <p className="mt-1 truncate text-sm text-fg-subtle">
            {scheduled
              ? `${formatVisitWhen(v.scheduled_for!)} · ${v.analyst_name} · ${modalityLabel(v.modality)}`
              : availabilityText(v.availability)}
          </p>
          {v.topic && <p className="mt-0.5 truncate text-xs text-fg-subtle">Assunto: {v.topic}</p>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Badge tone={STATUS_TONES[v.status] ?? 'neutral'}>{STATUS_LABELS[v.status] ?? v.status}</Badge>
          <span className="text-xs text-fg-subtle">{formatDate(v.requested_at)}</span>
        </div>
        <CaretRight
          aria-hidden
          className="size-4 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-gray-500"
        />
      </button>
    </li>
  );
}
